import { QueryKey, useQueryClient } from '@tanstack/react-query'

import { useAppMutation } from '@/helpers/api/mutation'
import { createInstance } from '@/helpers/api/base'
import { Log, LogUnfinishedMetadata, UpdatedLog } from '@/types/logs'
import { GET_FLAGGED_LOGS_KEY } from '@/pages/UnfinishedRecords/api/useGetFlaggedLogs'
import { GET_UPDATED_LOGS_KEY } from '@/pages/UnfinishedRecords/api/useGetUpdatedLogs'

interface EditFlagRecordRequest {
    flagMetadataId: number
    comment: string
    isFlagged?: boolean
}

interface MutateContext {
    previousEntries: [QueryKey, UpdatedLog[] | undefined][]
}

export const useEditFlagRecord = () => {
    const queryClient = useQueryClient()

    const { mutate, isPending } = useAppMutation<
        LogUnfinishedMetadata,
        Error,
        EditFlagRecordRequest,
        unknown,
        MutateContext | undefined
    >({
        mutationFn: ({ flagMetadataId, comment, isFlagged }) =>
            createInstance({
                url: `/controller-logs/flag/${flagMetadataId}`,
                method: 'PUT',
                data: { comment, isFlagged },
            }),
        alertOnError: true,
        onMutate: async ({ flagMetadataId, isFlagged }): Promise<MutateContext | undefined> => {
            if (isFlagged !== false) return

            await queryClient.cancelQueries({ queryKey: [GET_UPDATED_LOGS_KEY] })

            const previousEntries = queryClient.getQueriesData<UpdatedLog[]>({ queryKey: [GET_UPDATED_LOGS_KEY] })

            queryClient.setQueriesData<UpdatedLog[]>({ queryKey: [GET_UPDATED_LOGS_KEY] }, (prev) =>
                prev?.map((log) =>
                    log.unfinishedFlagMetadata?.id === flagMetadataId
                        ? { ...log, unfinishedFlagMetadata: { ...log.unfinishedFlagMetadata!, flag: false } }
                        : log
                )
            )

            return { previousEntries }
        },
        onError: (_err, { isFlagged }, context: MutateContext | undefined) => {
            if (isFlagged !== false || !context?.previousEntries) return
            for (const [queryKey, data] of context.previousEntries) {
                queryClient.setQueryData(queryKey, data)
            }
        },
        onSuccess: (data) => {
            const logUpdater = (prev: Log[] | undefined) =>
                prev?.map((log) =>
                    log.unfinishedFlagMetadata?.id === data.id ? { ...log, unfinishedFlagMetadata: data } : log
                )
            const updatedLogUpdater = (prev: UpdatedLog[] | undefined) =>
                prev?.map((log) =>
                    log.unfinishedFlagMetadata?.id === data.id ? { ...log, unfinishedFlagMetadata: data } : log
                )
            queryClient.setQueriesData<Log[]>({ queryKey: [GET_FLAGGED_LOGS_KEY] }, logUpdater)
            queryClient.setQueriesData<Log[]>({ queryKey: ['controllerLogs'] }, logUpdater)
            queryClient.setQueriesData<UpdatedLog[]>({ queryKey: [GET_UPDATED_LOGS_KEY] }, updatedLogUpdater)
            queryClient.invalidateQueries({ queryKey: [GET_UPDATED_LOGS_KEY] })
        },
    })

    return { mutate, isPending }
}
